import { Coding } from "@medplum/fhirtypes";

import mapValues from "lodash/mapValues";

import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";

import { FlagCategoryFilterValue, PracticeFlag } from "../interfaces/flags";

/**
 * What "in the library" and "on the chart" both mean. A deleted library entry and a flag removed
 * from a chart stay as inactive `Flag` resources, so `status` is part of every flag read, every
 * uniqueness guard, and every resource this context writes.
 */
export const ACTIVE_FLAG_STATUS = "active";

/** Shared so an empty library hands every consumer the same array on every render. */
export const NO_PRACTICE_FLAGS: PracticeFlag[] = [];

/**
 * Category and priority values ARE the codes persisted for a flag. They must stay in lockstep with
 * the concepts seeded in `app/fhir/seed-data/code-systems/qh-flag-category.json` and
 * `qh-flag-priority.json` — adding a member here without adding the concept there writes a coding
 * that resolves against nothing.
 *
 * Category lands on `Flag.category[]`. R4 has no `priority` element, so the priority coding falls
 * on `Flag.category[]` too, the two being told apart by system rather than by array order.
 */
export enum FlagCategory {
  Medical = "medical",
  Behavioral = "behavioral",
  Administrative = "administrative",
  Social = "social",
  Other = "other",
}

export enum FlagPriority {
  High = "high",
  Medium = "medium",
  Low = "low",
}

/**
 * The only place a code, its system and its display text are stated together. Build every written
 * coding and every rendered label from these; never derive either from the enum member name.
 */
export const FLAG_CATEGORY_CODINGS = {
  [FlagCategory.Medical]: {
    system: QuiltedHealthCodeSystems.FlagCategory,
    code: FlagCategory.Medical,
    display: "Medical",
  },
  [FlagCategory.Behavioral]: {
    system: QuiltedHealthCodeSystems.FlagCategory,
    code: FlagCategory.Behavioral,
    display: "Behavioral",
  },
  [FlagCategory.Administrative]: {
    system: QuiltedHealthCodeSystems.FlagCategory,
    code: FlagCategory.Administrative,
    display: "Administrative",
  },
  [FlagCategory.Social]: {
    system: QuiltedHealthCodeSystems.FlagCategory,
    code: FlagCategory.Social,
    display: "Social",
  },
  [FlagCategory.Other]: {
    system: QuiltedHealthCodeSystems.FlagCategory,
    code: FlagCategory.Other,
    display: "Other",
  },
} as const satisfies Record<FlagCategory, Coding>;

export const FLAG_PRIORITY_CODINGS = {
  [FlagPriority.High]: {
    system: QuiltedHealthCodeSystems.FlagPriority,
    code: FlagPriority.High,
    display: "High",
  },
  [FlagPriority.Medium]: {
    system: QuiltedHealthCodeSystems.FlagPriority,
    code: FlagPriority.Medium,
    display: "Medium",
  },
  [FlagPriority.Low]: {
    system: QuiltedHealthCodeSystems.FlagPriority,
    code: FlagPriority.Low,
    display: "Low",
  },
} as const satisfies Record<FlagPriority, Coding>;

/**
 * The enum members as arrays, for readers that have to narrow a stored code back to the enum.
 * Derived from the enums so a new member is covered without a second edit.
 */
export const FLAG_CATEGORY_VALUES = Object.values(FlagCategory);

export const FLAG_PRIORITY_VALUES = Object.values(FlagPriority);

export enum FlagSortKey {
  Name = "name",
  Category = "category",
  Priority = "priority",
  ChartCount = "chartCount",
}

/**
 * Pseudo-category for the filter row's first chip. Lowercase like the real codes, so no caller has
 * to case-fold it before using it as a value. It has no concept in either CodeSystem — it is a UI
 * value only, and its display comes from the label map below.
 */
export const ALL_CATEGORIES_FILTER = "all";

export const FLAG_CATEGORY_FILTERS: FlagCategoryFilterValue[] = [
  ALL_CATEGORIES_FILTER,
  ...FLAG_CATEGORY_VALUES,
];

/** Chip labels for the filter row, which carries the "All" pseudo-category alongside the codes. */
export const FLAG_CATEGORY_FILTER_LABELS: Record<FlagCategoryFilterValue, string> = {
  [ALL_CATEGORIES_FILTER]: "All",
  ...mapValues(FLAG_CATEGORY_CODINGS, ({ display }) => display),
};

/**
 * Status-chip triad from DESIGN.md §2.3 — tinted `/1` surface, vivid dot, `dark/9` label.
 * The label color never carries the hue, so every tier passes AA.
 */
export const FLAG_PRIORITY_CHIP_TOKENS: Record<FlagPriority, { surface: string; dot: string }> = {
  [FlagPriority.High]: { surface: "var(--mantine-color-red-1)", dot: "red.7" },
  [FlagPriority.Medium]: { surface: "var(--mantine-color-orange-1)", dot: "orange.5" },
  [FlagPriority.Low]: { surface: "var(--mantine-color-gray-2)", dot: "gray.5" },
};

/** High sorts above Medium above Low, rather than alphabetically. */
export const FLAG_PRIORITY_RANK: Record<FlagPriority, number> = {
  [FlagPriority.High]: 0,
  [FlagPriority.Medium]: 1,
  [FlagPriority.Low]: 2,
};

export const FLAG_SEARCH_PLACEHOLDER = "Search flags by name…";

/** Select data derived from the codings, so an option can never drift from the code it writes. */
const toSelectOptions = (codings: Record<string, { code: string; display: string }>) =>
  Object.values(codings).map(({ code, display }) => ({ value: code, label: display }));

export const FLAG_CATEGORY_OPTIONS = toSelectOptions(FLAG_CATEGORY_CODINGS);

export const FLAG_PRIORITY_OPTIONS = toSelectOptions(FLAG_PRIORITY_CODINGS);

export const FLAG_FORM_SELECT_PLACEHOLDER = "Select";

export const FLAG_DESCRIPTION_PLACEHOLDER = "What does this flag signal and when to apply it";
